import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { subscriptionDataService, subscriptionUtils, useSubscription } from '../services/subscriptionService.js';
import StripeSubscriptionPlans from '../components/StripeSubscriptionPlans';
import StripePayment from '../components/StripePayment';
import { ArrowLeft, CheckCircle, XCircle, Clock, CreditCard, FileText } from 'lucide-react';

const SubscriptionPage = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [employeeRole, setEmployeeRole] = useState(null);
  const navigate = useNavigate();

  const auth = getAuth();
  const user = auth.currentUser;
  const employeeId = user?.uid || '';

  const { getCurrentSubscription, getSubscriptionPlans } = useSubscription(db, employeeId);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);

      // 1. Récupérer le rôle et les données d'abonnement en parallèle
      const [employeeDoc, subscription, subscriptionInvoices, usedFreeTrial, history] = await Promise.all([
        getDoc(doc(db, 'employees', employeeId)),
        getCurrentSubscription(),
        subscriptionDataService.getEmployeeInvoices(db, employeeId),
        subscriptionDataService.hasUsedFreeTrial(db, employeeId),
        subscriptionDataService.getSubscriptionHistory(db, employeeId)
      ]);

      const employeeData = employeeDoc.exists() ? employeeDoc.data() : null;
      const role = employeeData?.role || null;
      setEmployeeRole(role);

      console.log('👤 Rôle de l\'utilisateur:', role);

      // 2. Vérifier si une action admin est nécessaire
      // Un admin doit avoir un abonnement actif. Si ce n'est pas le cas (ou si ce n'est pas un abo admin), on le crée.
      const isAdminUser = role === 'admin' || role === 'superadmin' || role === 'super_admin';
      const hasActiveAdminSub = subscription && subscription.status === 'active' && (subscription.planId === 'admin' || subscription.isAdmin);

      if (isAdminUser && !hasActiveAdminSub) {
        console.log('🔑 Utilisateur admin détecté sans abonnement actif, activation...');
        try {
          // Appeler la Cloud Function pour créer l'abonnement
          const result = await subscriptionDataService.createSubscriptionPayment(
            db,
            employeeId,
            'admin',
            null
          );

          console.log('✅ Accès admin activé:', result);
          setSuccessMessage('🎉 Accès administrateur activé automatiquement !');
          setTimeout(() => setSuccessMessage(null), 5000);

          // Recharger l'abonnement tout de suite après
          const newSubscription = await getCurrentSubscription();
          setCurrentSubscription(newSubscription);
        } catch (err) {
          console.error('❌ Erreur lors de l\'activation admin:', err);
        }
      } else {
        // Pas d'action nécessaire ou déjà correct
        setCurrentSubscription(subscription);
      }

      setInvoices(subscriptionInvoices);
      setHasUsedFreeTrial(usedFreeTrial);
      setSubscriptionHistory(history);

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    // Si l'utilisateur est admin/superadmin, ne pas afficher le formulaire de paiement
    if (employeeRole === 'admin' || employeeRole === 'superadmin' || employeeRole === 'super_admin') {
      setSuccessMessage('⚠️ En tant qu\'administrateur, vous avez déjà un accès illimité gratuit à toutes les fonctionnalités.');
      setTimeout(() => setSuccessMessage(null), 5000);
      setActiveTab('current');
      return;
    }

    if (plan.isFree) {
      // Attribution automatique du plan gratuit
      assignFreePlan(plan);
    } else if (plan.isOffered) {
      // Attribution automatique du plan offert
      assignOfferedPlan(plan);
    } else {
      setSelectedPlan(plan);
      setActiveTab('payment');
    }
  };

  const assignFreePlan = async (plan) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      console.log('🚀 Début de l\'attribution du plan gratuit pour:', employeeId);

      await subscriptionDataService.assignFreePlan(db, employeeId, plan.id);

      console.log('✅ Plan gratuit attribué avec succès');

      setSuccessMessage('🎉 Plan gratuit activé avec succès ! Vous avez maintenant accès aux fonctionnalités de base.');

      await loadSubscriptionData();
      setActiveTab('current');

      // Effacer le message de succès après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('❌ Erreur lors de l\'attribution du plan gratuit:', err);
      setError(err.message || 'Erreur lors de l\'attribution du plan gratuit. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };


  const assignOfferedPlan = async (plan) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      console.log('🚀 Début de l\'attribution du plan offert pour:', employeeId);

      await subscriptionDataService.assignOfferedPlan(db, employeeId, plan.id);

      console.log('✅ Plan offert attribué avec succès');

      setSuccessMessage('🎁 Accès offert activé avec succès ! Vous bénéficiez de toutes les fonctionnalités premium pendant 1 an.');

      await loadSubscriptionData();
      setActiveTab('current');

      // Effacer le message de succès après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('❌ Erreur lors de l\'attribution du plan offert:', err);
      setError(err.message || 'Erreur lors de l\'attribution du plan offert. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (subscription) => {
    setCurrentSubscription(subscription);
    setSelectedPlan(null);
    setActiveTab('current');
    loadSubscriptionData(); // Recharger les données
  };

  const handlePaymentCancel = () => {
    setSelectedPlan(null);
    setActiveTab('plans');
  };

  const getSubscriptionStatus = () => {
    if (!currentSubscription) {
      return { status: 'none', message: 'Aucun abonnement actif', color: 'text-gray-500' };
    }

    const isActive = subscriptionUtils.isSubscriptionActive(currentSubscription);
    const isFree = subscriptionUtils.isFreeSubscription(currentSubscription);
    const isOffered = subscriptionUtils.isOfferedSubscription(currentSubscription);

    if (!isActive) {
      return { status: 'expired', message: 'Abonnement expiré', color: 'text-red-500' };
    }

    if (isOffered) {
      return { status: 'offered', message: 'Accès offert actif', color: 'text-purple-500' };
    }

    if (isFree) {
      return { status: 'free', message: 'Plan gratuit actif', color: 'text-green-500' };
    }

    return { status: 'premium', message: 'Abonnement premium actif', color: 'text-blue-500' };
  };

  const renderCurrentSubscription = () => {
    const status = getSubscriptionStatus();

    return (
      <div className="space-y-6">
        {/* Statut de l'abonnement */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Votre abonnement</h3>
            <div className={`flex items-center ${status.color}`}>
              {status.status === 'premium' && <CheckCircle className="w-5 h-5 mr-2" />}
              {status.status === 'offered' && <CheckCircle className="w-5 h-5 mr-2" />}
              {status.status === 'free' && <CheckCircle className="w-5 h-5 mr-2" />}
              {status.status === 'expired' && <XCircle className="w-5 h-5 mr-2" />}
              {status.status === 'none' && <Clock className="w-5 h-5 mr-2" />}
              <span className="font-medium">{status.message}</span>
            </div>
          </div>

          {currentSubscription && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Type d'abonnement</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentSubscription.status === 'free'
                    ? 'Gratuit'
                    : currentSubscription.status === 'offered'
                      ? 'Offert (Premium)'
                      : 'Premium'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Prix</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {subscriptionUtils.formatPrice(currentSubscription.price)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Début de période</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(currentSubscription.currentPeriodStart).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Fin de période</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          )}

          {status.status === 'expired' && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                Votre abonnement a expiré. Veuillez souscrire à un nouveau plan pour continuer à utiliser toutes les fonctionnalités.
              </p>
              <button
                onClick={() => setActiveTab('plans')}
                className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded transition-colors"
              >
                Voir les plans
              </button>
            </div>
          )}
        </div>

        {/* Historique des abonnements */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Historique des abonnements</h3>
            <Clock className="w-5 h-5 text-gray-500" />
          </div>

          {subscriptionHistory.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Aucun historique d'abonnement
            </p>
          ) : (
            <div className="space-y-3">
              {subscriptionHistory.map((sub) => (
                <div
                  key={sub.id}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {sub.status === 'free' ? 'Plan Découverte' :
                            sub.status === 'offered' ? 'Premium Offert' :
                              sub.planId === 'yearly' ? 'Essentiel Annuel' : 'Essentiel'}
                        </h4>
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${sub.status === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                              : sub.status === 'free'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                : sub.status === 'offered'
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                  : sub.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                            }`}
                        >
                          {sub.status === 'active' ? 'Actif' :
                            sub.status === 'free' ? 'Gratuit' :
                              sub.status === 'offered' ? 'Offert' :
                                sub.status === 'cancelled' ? 'Annulé' :
                                  'Expiré'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Du {new Date(sub.currentPeriodStart).toLocaleDateString('fr-FR')} au {new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Créé le {new Date(sub.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 dark:text-white">
                        {subscriptionUtils.formatPrice(sub.price || 0)}
                      </p>
                      {sub.price > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {sub.planId === 'yearly' ? '/an' : '/mois'}
                        </p>
                      )}
                    </div>
                  </div>

                  {sub.cancelledAt && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                      Annulé le {new Date(sub.cancelledAt).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Historique des factures */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Factures</h3>
            <FileText className="w-5 h-5 text-gray-500" />
          </div>

          {invoices.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Aucune facture disponible
            </p>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {invoice.invoiceNumber}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(invoice.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {subscriptionUtils.formatPrice(invoice.totalAmount)}
                    </p>
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded-full ${invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : invoice.status === 'overdue'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                        }`}
                    >
                      {invoice.status === 'paid' ? 'Payée' : invoice.status === 'overdue' ? 'En retard' : 'En attente'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPlans = () => (
    <div className="space-y-8">
      {/* En-tête amélioré */}
      <div className="text-center max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          Accédez à toutes les fonctionnalités
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
          Choisissez le plan qui correspond à vos besoins professionnels
        </p>
        <div className="flex flex-wrap justify-center gap-4 text-sm">
          <div className="flex items-center text-gray-700 dark:text-gray-300">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
            Sans engagement
          </div>
          <div className="flex items-center text-gray-700 dark:text-gray-300">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
            30 jours d'essai gratuit
          </div>
          <div className="flex items-center text-gray-700 dark:text-gray-300">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
            Annulation en 1 clic
          </div>
        </div>
      </div>


      <StripeSubscriptionPlans
        employeeId={employeeId}
        onPlanSelect={handlePlanSelect}
        currentSubscription={currentSubscription}
        showOfferedPlan={false}
        hasUsedFreeTrial={hasUsedFreeTrial}
      />

      {/* Section FAQ rapide */}
      <div className="mt-12 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
          Questions fréquentes
        </h3>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              Puis-je changer de plan ?
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Oui, vous pouvez passer au plan supérieur ou inférieur à tout moment. Le changement est effectif immédiatement.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              Comment annuler mon abonnement ?
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vous pouvez annuler votre abonnement en 1 clic depuis votre espace. Aucune question posée, aucun frais caché.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              Mes données sont-elles sécurisées ?
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vos données sont chiffrées et hébergées en France. Nous sommes conformes RGPD et ne partageons jamais vos informations.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              Y a-t-il des frais cachés ?
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Non, le prix affiché est le prix final. Pas de frais d'installation, pas de frais de mise en service.
            </p>
          </div>
        </div>
      </div>

      {/* Besoin d'aide */}
      <div className="text-center mt-8">
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          Besoin d'aide pour choisir ?
        </p>
        <button className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
          Contactez notre équipe
        </button>
      </div>
    </div>
  );

  const renderPayment = () => {
    if (!selectedPlan) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">Aucun plan sélectionné</p>
          <button
            onClick={() => setActiveTab('plans')}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Retour aux plans
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Finaliser votre abonnement
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {selectedPlan.name} - {subscriptionUtils.formatPrice(selectedPlan.price)}
          </p>
        </div>

        <StripePayment
          plan={selectedPlan}
          employeeId={employeeId}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto p-4">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft size={24} className="text-gray-700 dark:text-gray-300" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            Gestion d'abonnement
          </h1>
          <div className="w-10"></div>
        </div>

        {/* Navigation par onglets */}
        <div className="flex overflow-x-auto space-x-1 mb-6 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          {[
            { id: 'current', label: 'Mon abonnement', icon: CheckCircle },
            { id: 'plans', label: 'Plans disponibles', icon: CreditCard },
            { id: 'payment', label: 'Paiement', icon: FileText, hidden: !selectedPlan }
          ].map(tab => {
            if (tab.hidden) return null;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md whitespace-nowrap transition-colors
                         ${activeTab === tab.id
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-500 dark:text-blue-400'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'}`}
              >
                <tab.icon size={18} className={activeTab === tab.id ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                <span className="text-sm md:text-base">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Contenu principal */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          {activeTab === 'current' && renderCurrentSubscription()}
          {activeTab === 'plans' && renderPlans()}
          {activeTab === 'payment' && renderPayment()}
        </div>

        {/* Messages de succès */}
        {successMessage && (
          <div className="fixed bottom-4 right-4 bg-green-100 dark:bg-green-900 border-l-4 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300 p-4 rounded shadow-lg max-w-md">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          </div>
        )}

        {/* Messages d'erreur */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300 p-4 rounded shadow-lg max-w-md">
            <div className="flex items-start">
              <XCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
